import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate, Link } from 'react-router-dom';
import GoogleWebButton, { googleNonce } from '../components/GoogleWebButton';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import PageMeta from '../components/PageMeta';
import PasswordInput from '../components/PasswordInput';
import {
  isNativePlatform, isNativeGoogleAuthAvailable, nativeGoogleSignIn,
  isNativeAppleAuthAvailable, nativeAppleSignIn,
} from '../lib/nativeSocialAuth';
import { GoogleSignInButton, AppleSignInButton } from '../components/SocialAuthButtons';
import AppleEmailPrompt from '../components/AppleEmailPrompt';
import { isBiometricLoginAvailable, signInWithBiometrics, enrollBiometricLogin } from '../lib/biometricLogin';
import { authErrorMessage } from '../lib/authErrorMessage';
import useContainerWidth from '../hooks/useContainerWidth';
import { googleButtonWidth } from '../lib/googleButtonWidth';

export default function Login() {
  // The Google button can't stretch with CSS; it's sized to this container (lib/googleButtonWidth.js).
  const [googleRef, googleContainerWidth] = useContainerWidth();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const nonce = googleNonce();

  // TOTP second-factor state
  const [stepToken, setStepToken] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false); // recovery: enter a backup code instead of a TOTP code

  // Face ID / fingerprint sign-in (v1.0.2 §4), the way banking apps do it: the link is there on
  // any phone that can use biometrics. A tap goes straight to the protected read, the one prompt
  // the user asked for. If nothing is stored, the read fails fast with no prompt ('unavailable'),
  // and the user is asked to sign in once by any method; that sign-in turns it on (finishLogin)
  // with the enroll pass every full sign-in returns, so 2FA users aren't asked for a second code.
  // Nothing on this page touches the protected item before a tap: checking it prompts on iOS.
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricNotice, setBiometricNotice] = useState('');
  const enrollAfterLogin = useRef(false);
  useEffect(() => {
    let alive = true;
    isBiometricLoginAvailable().then(available => { if (alive) setBiometricAvailable(available); });
    return () => { alive = false; };
  }, []);
  const biometricName = Capacitor.getPlatform() === 'ios' ? 'Face ID' : 'biometrics';

  // Every successful sign-in ends here, whichever method it used.
  async function finishLogin(data) {
    login(data.token);
    if (enrollAfterLogin.current && data.enrollToken) {
      enrollAfterLogin.current = false;
      // A declined or failed setup never blocks the sign-in; Settings can turn it on later.
      await enrollBiometricLogin({ enrollToken: data.enrollToken });
    }
    navigate('/');
  }

  function askToSetUp() {
    enrollAfterLogin.current = true;
    setBiometricNotice(`Sign in once below to turn on ${biometricName}. After that, ${biometricName} signs you in.`);
  }

  async function handleBiometricSignIn() {
    setError('');
    setBiometricNotice('');
    setBiometricLoading(true);
    const result = await signInWithBiometrics();
    setBiometricLoading(false);
    if (result.ok) {
      login(result.token);
      navigate('/');
      return;
    }
    if (result.reason === 'cancelled') return;
    if (result.reason === 'unavailable') {
      askToSetUp(); // nothing stored on this phone (never set up, or invalidated by a new face/finger)
      return;
    }
    if (result.reason === 'network') {
      setError("Couldn't reach Agence. Check your connection and try again.");
      return;
    }
    setError(result.reason === 'revoked'
      ? 'Biometric sign-in was turned off on this device. Sign in with your password, then turn it back on in Settings.'
      : "Biometric sign-in isn't set up on this device anymore. Sign in with your password, then turn it back on in Settings.");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.requiresTotp) {
        setStepToken(data.stepToken);
      } else {
        await finishLogin(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  // Both authenticator codes and backup codes verify against /auth/totp/login —
  // the server tries the TOTP code first, then falls back to a backup-code lookup.
  async function handleTotpSubmit(e) {
    e.preventDefault();
    setError('');
    setTotpLoading(true);
    try {
      const { data } = await api.post('/auth/totp/login', { stepToken, code: totpCode });
      await finishLogin(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
      setTotpCode('');
    } finally {
      setTotpLoading(false);
    }
  }

  // `nonce` only means anything for the web Google button below, which is the one
  // thing that actually requests a token using it. Native sign-in (both providers) gets its
  // token from the OS SDK directly, with no way to make it embed this React-side nonce — so
  // sending it along anyway just guarantees a nonce-mismatch rejection on the backend, since
  // it can never equal whatever (if anything) ends up in the native token's own nonce claim.
  async function handleGoogleSuccess({ credential }, { includeNonce = true } = {}) {
    setError('');
    try {
      const body = includeNonce ? { credential, nonce } : { credential };
      const { data } = await api.post('/auth/google', body);
      if (data.requiresTotp) {
        setStepToken(data.stepToken); // user enabled TOTP — clear the 2FA challenge before login
      } else {
        await finishLogin(data);
      }
    } catch (err) {
      setError(authErrorMessage(err, 'Google sign-in failed'));
    }
  }

  async function handleNativeGoogleClick() {
    setError('');
    try {
      const result = await nativeGoogleSignIn();
      await handleGoogleSuccess(result, { includeNonce: false });
    } catch (err) {
      // The native plugin's own errors (CustomExceptions.java) carry a real diagnostic
      // message even for what looks like a cancellation — Google Play services reports
      // Android OAuth-client misconfiguration as a cancellation too, and the plugin
      // deliberately preserves the original message so it stays diagnosable. A bare
      // catch here used to throw that away, leaving no way to tell "user tapped cancel"
      // apart from "no Android OAuth client matches this app's SHA-1/package" without a
      // rebuild-and-attach-a-debugger cycle.
      //
      // Note this catch does NOT see failures of the POST inside handleGoogleSuccess —
      // that function handles its own errors and does not rethrow. Both paths go through
      // authErrorMessage() so a request that never completed reads as a network problem
      // rather than as "sign-in failed", which is what made a real Android report
      // (2026-09-18, closed testing) impossible to act on.
      setError(authErrorMessage(err, 'Google sign-in failed'));
    }
  }

  // Apple gives email/name only on the FIRST authorization for an Apple ID + app, so the
  // server can answer `{ requiresEmail: true }` (see /auth/apple step 4). Hold the identity
  // token so the same one can be re-sent with the address the user types — it is re-verified
  // server-side, so nothing here is trusted. Apple's tokens are short-lived, which is why a
  // stale-token failure sends the user back to the button rather than retrying silently.
  const [applePending, setApplePending] = useState(null);
  const [appleEmail, setAppleEmail] = useState('');
  const [appleBusy, setAppleBusy] = useState(false);

  async function finishAppleSession(data) {
    if (data.requiresTotp) {
      setStepToken(data.stepToken);
    } else {
      await finishLogin(data);
    }
  }

  async function handleAppleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    setAppleBusy(true);
    try {
      const { data } = await api.post('/auth/apple', { ...applePending, email: appleEmail.trim() });
      if (data.requiresEmail) {
        setError('Please enter a valid email address.');
        return;
      }
      setApplePending(null);
      setAppleEmail('');
      finishAppleSession(data);
    } catch (err) {
      const status = err.response?.status;
      setError(status === 401
        // The identity token expired while the form was open — a fresh one is the only fix.
        ? 'That took too long — tap Continue with Apple again to retry.'
        : err.response?.data?.error || 'Could not finish signing in');
      if (status === 401) { setApplePending(null); setAppleEmail(''); }
    } finally {
      setAppleBusy(false);
    }
  }

  async function handleNativeAppleClick() {
    setError('');
    try {
      const { identityToken, fullName } = await nativeAppleSignIn();
      const { data } = await api.post('/auth/apple', { identityToken, fullName });
      if (data.requiresEmail) {
        setApplePending({ identityToken, fullName });
        return;
      }
      finishAppleSession(data);
    } catch (err) {
      setError(authErrorMessage(err, 'Apple sign-in failed'));
    }
  }

  // Checked before the TOTP gate: an Apple sign-in that never produced a session can't have
  // reached a 2FA challenge, so the two states are mutually exclusive.
  if (applePending) {
    return (
      <AppleEmailPrompt
        email={appleEmail}
        onEmailChange={e => setAppleEmail(e.target.value)}
        onSubmit={handleAppleEmailSubmit}
        onCancel={() => { setApplePending(null); setAppleEmail(''); setError(''); }}
        error={error}
        busy={appleBusy}
      />
    );
  }

  if (stepToken) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>Agence</h1>
          <h2>Two-factor verification</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
            {useBackupCode
              ? 'Enter one of your backup codes.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
          <form onSubmit={handleTotpSubmit}>
            {useBackupCode ? (
              <input
                type="text"
                aria-label="Backup code"
                placeholder="XXXXX-XXXXX"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 11))}
                autoFocus
                autoComplete="one-time-code"
                style={{ letterSpacing: '0.15em', textAlign: 'center', fontSize: '1.2rem' }}
                required
                disabled={totpLoading}
              />
            ) : (
              <input
                type="text"
                inputMode="numeric"
                aria-label="6-digit authentication code"
                placeholder=""
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
                autoComplete="one-time-code"
                style={{ letterSpacing: '0.25em', textAlign: 'center', fontSize: '1.4rem' }}
                required
                disabled={totpLoading}
              />
            )}
            {error && <p className="error">{error}</p>}
            <button type="submit" disabled={totpLoading || (useBackupCode ? totpCode.replace(/-/g, '').length < 10 : totpCode.length !== 6)}>
              {totpLoading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
          <button
            onClick={() => { setUseBackupCode(v => !v); setError(''); setTotpCode(''); }}
            style={{ background: 'none', border: 'none', color: 'var(--navy-600)', cursor: 'pointer', fontSize: '0.85rem', marginTop: '0.75rem', textDecoration: 'underline', display: 'block' }}
          >
            {useBackupCode ? 'Use authenticator app instead' : "Can't access your authenticator? Use a backup code"}
          </button>
          <button
            onClick={() => { setStepToken(null); setError(''); setTotpCode(''); setUseBackupCode(false); }}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <PageMeta title="Sign In" description="Sign in to Agence, the AI personal finance and investment copilot." path="/login" />
      <div className="auth-card">
        <h1>Agence</h1>
        <h2>Sign in</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            autoComplete="email"
            aria-label="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <PasswordInput
            placeholder="Password"
            autoComplete="current-password"
            aria-label="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          {biometricAvailable && (
            <div className="auth-biometric">
              <button type="button" className="auth-biometric-link" onClick={handleBiometricSignIn} disabled={biometricLoading}>
                {biometricLoading ? 'Signing in…' : `Use ${biometricName}`}
              </button>
              {biometricNotice && <p className="auth-biometric-notice" role="status">{biometricNotice}</p>}
            </div>
          )}
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="auth-divider"><span>or</span></div>
        <div className="auth-google" ref={googleRef}>
          {isNativePlatform() ? (
            isNativeGoogleAuthAvailable() && <GoogleSignInButton onClick={handleNativeGoogleClick} />
          ) : (
            <GoogleWebButton
              onSuccess={handleGoogleSuccess}
              onError={() => setError('Google sign-in failed')}
              width={googleButtonWidth(googleContainerWidth)}
            />
          )}
          {isNativeAppleAuthAvailable() && <AppleSignInButton onClick={handleNativeAppleClick} />}
        </div>
        <p style={{ marginTop: '0.5rem' }}>No account? <Link to="/register">Register</Link></p>
        <p style={{ marginTop: '0.25rem', fontSize: '0.85rem' }}><Link to="/forgot-password">Forgot password?</Link></p>
      </div>
    </div>
  );
}
